import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from '@/components/layout/app-shell';
import { LoginPage } from '@/features/auth/login-page';
import { RequireAnonymous, RequireAuth, RequirePermission } from '@/features/auth/protected-route';
import { DashboardPage } from '@/features/dashboard/dashboard-page';
import { Skeleton } from '@/components/ui/skeleton';

/*
 * Sign-in and the dashboard are in the main bundle because every session
 * starts at one of them. Everything else is fetched on first visit, which
 * keeps the initial download to what a user actually needs to get going.
 */
const RegisterPage = lazyRoute(() => import('@/features/auth/register-page'), 'RegisterPage');
const AcceptInvitePage = lazyRoute(
  () => import('@/features/auth/accept-invite-page'),
  'AcceptInvitePage',
);
const UsersPage = lazyRoute(() => import('@/features/users/users-page'), 'UsersPage');
const BackupsPage = lazyRoute(() => import('@/features/settings/backups-page'), 'BackupsPage');
const DuplicatesPage = lazyRoute(
  () => import('@/features/duplicates/duplicates-page'),
  'DuplicatesPage',
);
const OrganizationSettingsPage = lazyRoute(
  () => import('@/features/settings/organization-page'),
  'OrganizationSettingsPage',
);
const ProfilePage = lazyRoute(() => import('@/features/settings/profile-page'), 'ProfilePage');
const AuditLogPage = lazyRoute(() => import('@/features/audit/audit-log-page'), 'AuditLogPage');
const ModuleBuilderPage = lazyRoute(
  () => import('@/features/custom-modules/module-builder-page'),
  'ModuleBuilderPage',
);
const CustomModuleListPage = lazyRoute(
  () => import('@/features/custom-modules/custom-module-pages'),
  'CustomModuleListPage',
);
const CustomRecordDetailPage = lazyRoute(
  () => import('@/features/custom-modules/custom-module-pages'),
  'CustomRecordDetailPage',
);
const BillingSettingsPage = lazyRoute(
  () => import('@/features/settings/billing-settings-page'),
  'BillingSettingsPage',
);
const NotFoundPage = lazyRoute(() => import('@/features/not-found-page'), 'NotFoundPage');

// CRM modules. Each is its own chunk, so a rep who lives in the pipeline never
// downloads the accounts screens.
const LeadsPage = lazyRoute(() => import('@/features/leads/leads-page'), 'LeadsPage');
const LeadDetailPage = lazyRoute(
  () => import('@/features/leads/lead-detail-page'),
  'LeadDetailPage',
);
const AccountsPage = lazyRoute(() => import('@/features/accounts/accounts-page'), 'AccountsPage');
const AccountDetailPage = lazyRoute(
  () => import('@/features/accounts/account-detail-page'),
  'AccountDetailPage',
);
const ContactsPage = lazyRoute(() => import('@/features/contacts/contacts-page'), 'ContactsPage');
const ContactDetailPage = lazyRoute(
  () => import('@/features/contacts/contact-detail-page'),
  'ContactDetailPage',
);
const DealsPage = lazyRoute(() => import('@/features/deals/deals-page'), 'DealsPage');
const DealDetailPage = lazyRoute(
  () => import('@/features/deals/deal-detail-page'),
  'DealDetailPage',
);
const TasksPage = lazyRoute(
  () => import('@/features/activities/activity-section-page'),
  'TasksPage',
);
const MeetingsPage = lazyRoute(
  () => import('@/features/activities/activity-section-page'),
  'MeetingsPage',
);
const CallsPage = lazyRoute(
  () => import('@/features/activities/activity-section-page'),
  'CallsPage',
);

// Automation & insight.
const ReportsPage = lazyRoute(() => import('@/features/reports/reports-page'), 'ReportsPage');
const ApprovalsPage = lazyRoute(
  () => import('@/features/approvals/approvals-page'),
  'ApprovalsPage',
);
const WorkflowsPage = lazyRoute(
  () => import('@/features/automation/workflows-page'),
  'WorkflowsPage',
);
const AssignmentRulesPage = lazyRoute(
  () => import('@/features/automation/assignment-rules-page'),
  'AssignmentRulesPage',
);
const CustomFieldsPage = lazyRoute(
  () => import('@/features/automation/custom-fields-page'),
  'CustomFieldsPage',
);
const EmailTemplatesPage = lazyRoute(
  () => import('@/features/email/email-templates-page'),
  'EmailTemplatesPage',
);
const ImportPage = lazyRoute(() => import('@/features/import/import-page'), 'ImportPage');
const ItemListsPage = lazyRoute(() => import('@/features/item-lists/item-lists-page'), 'ItemListsPage');
const ItemListDetailPage = lazyRoute(
  () => import('@/features/item-lists/item-list-detail-page'),
  'ItemListDetailPage',
);
const ProductsPage = lazyRoute(() => import('@/features/modules/catalogue-pages'), 'ProductsPage');
const BooksDashboardPage = lazyRoute(() => import('@/features/books/books-dashboard-page'), 'BooksDashboardPage');
const BooksSettingsPage = lazyRoute(() => import('@/features/books/books-settings-page'), 'BooksSettingsPage');
const VendorDetailPage = lazyRoute(() => import('@/features/books/vendor-detail-page'), 'VendorDetailPage');
const PurchaseOrdersPage = lazyRoute(() => import('@/features/books/purchase-order-pages'), 'PurchaseOrdersPage');
const PurchaseOrderFormPage = lazyRoute(() => import('@/features/books/purchase-order-form-page'), 'PurchaseOrderFormPage');
const PurchaseOrderDetailPage = lazyRoute(() => import('@/features/books/purchase-order-detail-page'), 'PurchaseOrderDetailPage');
const ExpenseReportsPage = lazyRoute(() => import('@/features/books/expense-report-pages'), 'ExpenseReportsPage');
const ExpenseReportFormPage = lazyRoute(() => import('@/features/books/expense-report-form-page'), 'ExpenseReportFormPage');
const ExpenseReportDetailPage = lazyRoute(() => import('@/features/books/expense-report-detail-page'), 'ExpenseReportDetailPage');
const GstInvoicesPage = lazyRoute(() => import('@/features/books/gst-invoice-pages'), 'GstInvoicesPage');
const GstInvoiceFormPage = lazyRoute(() => import('@/features/books/gst-invoice-form-page'), 'GstInvoiceFormPage');
const GstInvoiceDetailPage = lazyRoute(() => import('@/features/books/gst-invoice-detail-page'), 'GstInvoiceDetailPage');
const ReportsLayout = lazyRoute(() => import('@/features/finance-reports/reports-layout'), 'ReportsLayout');
const ReportsLandingPage = lazyRoute(() => import('@/features/finance-reports/reports-landing-page'), 'ReportsLandingPage');
const FinanceReportPage = lazyRoute(() => import('@/features/finance-reports/report-page'), 'ReportPage');
const ScheduledReportsPage = lazyRoute(() => import('@/features/finance-reports/schedules-page'), 'ScheduledReportsPage');
const ReportDeliveriesPage = lazyRoute(() => import('@/features/finance-reports/schedules-page'), 'DeliveriesPage');
const GstReportPage = lazyRoute(() => import('@/features/books/gst-report-page'), 'GstReportPage');
const VendorsPage = lazyRoute(() => import('@/features/modules/catalogue-pages'), 'VendorsPage');
const CasesPage = lazyRoute(() => import('@/features/modules/support-pages'), 'CasesPage');
const CaseDetailPage = lazyRoute(
  () => import('@/features/modules/case-detail-page'),
  'CaseDetailPage',
);
const SolutionsPage = lazyRoute(() => import('@/features/modules/support-pages'), 'SolutionsPage');
const CampaignsPage = lazyRoute(() => import('@/features/modules/support-pages'), 'CampaignsPage');
const QuotesPage = lazyRoute(() => import('@/features/modules/sales-document-pages'), 'QuotesPage');
const SalesOrdersPage = lazyRoute(
  () => import('@/features/modules/sales-document-pages'),
  'SalesOrdersPage',
);
const InvoicesPage = lazyRoute(
  () => import('@/features/modules/sales-document-pages'),
  'InvoicesPage',
);
const QuoteDetailPage = lazyRoute(
  () => import('@/features/modules/sales-document-detail-pages'),
  'QuoteDetailPage',
);
const SalesOrderDetailPage = lazyRoute(
  () => import('@/features/modules/sales-document-detail-pages'),
  'SalesOrderDetailPage',
);
const InvoiceDetailPage = lazyRoute(
  () => import('@/features/modules/sales-document-detail-pages'),
  'InvoiceDetailPage',
);
const SavedReportsPage = lazyRoute(
  () => import('@/features/reports/saved-reports-page'),
  'SavedReportsPage',
);
const ReportBuilderPage = lazyRoute(
  () => import('@/features/reports/report-builder-page'),
  'ReportBuilderPage',
);
const SavedReportViewPage = lazyRoute(
  () => import('@/features/reports/saved-report-view-page'),
  'SavedReportViewPage',
);
const DocumentTemplatesPage = lazyRoute(
  () => import('@/features/documents/document-templates-page'),
  'DocumentTemplatesPage',
);
const PaymentsPage = lazyRoute(() => import('@/features/payments/payments-page'), 'PaymentsPage');
const ExpensesPage = lazyRoute(() => import('@/features/expenses/expenses-page'), 'ExpensesPage');
const ExpenseDetailPage = lazyRoute(
  () => import('@/features/expenses/expense-detail-page'),
  'ExpenseDetailPage',
);
const RecurringExpensesPage = lazyRoute(
  () => import('@/features/recurring-expenses/recurring-expenses-page'),
  'RecurringExpensesPage',
);
const RecurringExpenseDetailPage = lazyRoute(
  () => import('@/features/recurring-expenses/recurring-expense-detail-page'),
  'RecurringExpenseDetailPage',
);
const CreditNotesPage = lazyRoute(
  () => import('@/features/credit-notes/credit-notes-page'),
  'CreditNotesPage',
);
const CreditNoteDetailPage = lazyRoute(
  () => import('@/features/credit-notes/credit-note-detail-page'),
  'CreditNoteDetailPage',
);
const PaymentDetailPage = lazyRoute(
  () => import('@/features/payments/payment-detail-page'),
  'PaymentDetailPage',
);
const ZohoImportPage = lazyRoute(
  () => import('@/features/zoho/zoho-import-page'),
  'ZohoImportPage',
);
const BankAccountsPage = lazyRoute(
  () => import('@/features/banking/bank-accounts-page'),
  'BankAccountsPage',
);
const BankAccountDetailPage = lazyRoute(
  () => import('@/features/banking/bank-account-detail-page'),
  'BankAccountDetailPage',
);
const BankingReportsPage = lazyRoute(
  () => import('@/features/banking/banking-reports-page'),
  'BankingReportsPage',
);

/** React.lazy expects a default export; these modules use named ones. */
function lazyRoute<K extends string>(
  loader: () => Promise<Record<K, ComponentType>>,
  exportName: K,
): LazyExoticComponent<ComponentType> {
  return lazy(async () => ({ default: (await loader())[exportName] }));
}

/** Placeholder shown while a route chunk downloads. */
function RouteFallback() {
  return (
    <div className="space-y-4 p-6" role="status" aria-live="polite">
      <span className="sr-only-focusable">Loading page…</span>
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}

/** Wraps a lazily-loaded element in the Suspense boundary it needs. */
function suspended(Component: LazyExoticComponent<ComponentType>) {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Component />
    </Suspense>
  );
}

/**
 * Routes mirror the permission model: anonymous-only, authenticated, and
 * authenticated-with-a-capability. The nesting means a guard is applied by
 * position rather than remembered on each individual screen.
 */
export const router = createBrowserRouter([
  {
    element: <RequireAnonymous />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/register', element: suspended(RegisterPage) },
    ],
  },
  // Reachable whether or not a session exists: the link arrives by email.
  { path: '/accept-invite', element: suspended(AcceptInvitePage) },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <DashboardPage /> },

          {
            element: <RequirePermission permission="lead:read" />,
            children: [
              { path: '/leads', element: suspended(LeadsPage) },
              { path: '/leads/:id', element: suspended(LeadDetailPage) },
            ],
          },
          {
            element: <RequirePermission permission="account:read" />,
            children: [
              { path: '/accounts', element: suspended(AccountsPage) },
              { path: '/accounts/:id', element: suspended(AccountDetailPage) },
            ],
          },
          {
            element: <RequirePermission permission="contact:read" />,
            children: [
              { path: '/contacts', element: suspended(ContactsPage) },
              { path: '/contacts/:id', element: suspended(ContactDetailPage) },
            ],
          },
          {
            element: <RequirePermission permission="deal:read" />,
            children: [
              { path: '/deals', element: suspended(DealsPage) },
              { path: '/deals/:id', element: suspended(DealDetailPage) },
            ],
          },
          {
            element: <RequirePermission permission="activity:read" />,
            children: [
              { path: '/tasks', element: suspended(TasksPage) },
              { path: '/meetings', element: suspended(MeetingsPage) },
              { path: '/calls', element: suspended(CallsPage) },
              // Activities are three sections now. The old path still works,
              // because it is in bookmarks and in the keyboard shortcut.
              { path: '/activities', element: <Navigate to="/tasks" replace /> },
            ],
          },
          {
            element: <RequirePermission permission="report:read" />,
            children: [
              { path: '/reports', element: suspended(ReportsPage) },
              { path: '/reports/custom', element: suspended(SavedReportsPage) },
              { path: '/reports/custom/new', element: suspended(ReportBuilderPage) },
              { path: '/reports/custom/:id', element: suspended(ReportBuilderPage) },
              { path: '/reports/custom/:id/view', element: suspended(SavedReportViewPage) },
            ],
          },

          // The Zoho parity modules. Each rides on its own read permission, so
          // a role that cannot see invoices does not get a dead link to them.
          {
            element: <RequirePermission permission="itemList:read" />,
            children: [
              { path: '/item-lists', element: suspended(ItemListsPage) },
              { path: '/item-lists/:id', element: suspended(ItemListDetailPage) },
            ],
          },
          {
            element: <RequirePermission permission="product:read" />,
            children: [{ path: '/products', element: suspended(ProductsPage) }],
          },
          {
            element: <RequirePermission permission="vendor:read" />,
            children: [
              { path: '/vendors', element: suspended(VendorsPage) },
              { path: '/vendors/:id', element: suspended(VendorDetailPage) },
            ],
          },
          { path: '/books', element: suspended(BooksDashboardPage) },
          {
            element: <RequirePermission permission="financeReport:read" />,
            children: [
              {
                path: '/books/reports',
                element: suspended(ReportsLayout),
                children: [
                  { index: true, element: suspended(ReportsLandingPage) },
                  { path: 'deliveries', element: suspended(ReportDeliveriesPage) },
                  {
                    element: <RequirePermission permission="financeReport:manage" />,
                    children: [{ path: 'schedules', element: suspended(ScheduledReportsPage) }],
                  },
                  { path: ':reportId', element: suspended(FinanceReportPage) },
                ],
              },
            ],
          },
          {
            element: <RequirePermission permission="booksSettings:read" />,
            children: [{ path: '/settings/books', element: suspended(BooksSettingsPage) }],
          },
          {
            element: <RequirePermission permission="purchaseOrder:read" />,
            children: [
              { path: '/purchase-orders', element: suspended(PurchaseOrdersPage) },
              { path: '/purchase-orders/:id', element: suspended(PurchaseOrderDetailPage) },
            ],
          },
          {
            element: <RequirePermission permission="purchaseOrder:create" />,
            children: [{ path: '/purchase-orders/new', element: suspended(PurchaseOrderFormPage) }],
          },
          {
            element: <RequirePermission permission="purchaseOrder:update" />,
            children: [{ path: '/purchase-orders/:id/edit', element: suspended(PurchaseOrderFormPage) }],
          },
          {
            element: <RequirePermission permission="expenseReport:read" />,
            children: [
              { path: '/expense-reports', element: suspended(ExpenseReportsPage) },
              { path: '/expense-reports/:id', element: suspended(ExpenseReportDetailPage) },
            ],
          },
          {
            element: <RequirePermission permission="expenseReport:create" />,
            children: [{ path: '/expense-reports/new', element: suspended(ExpenseReportFormPage) }],
          },
          {
            element: <RequirePermission permission="expenseReport:update" />,
            children: [{ path: '/expense-reports/:id/edit', element: suspended(ExpenseReportFormPage) }],
          },
          {
            element: <RequirePermission permission="gstInvoice:read" />,
            children: [
              { path: '/gst-invoices', element: suspended(GstInvoicesPage) },
              { path: '/gst-invoices/report', element: suspended(GstReportPage) },
              { path: '/gst-invoices/:id', element: suspended(GstInvoiceDetailPage) },
            ],
          },
          {
            element: <RequirePermission permission="gstInvoice:create" />,
            children: [{ path: '/gst-invoices/new', element: suspended(GstInvoiceFormPage) }],
          },
          {
            element: <RequirePermission permission="gstInvoice:update" />,
            children: [{ path: '/gst-invoices/:id/edit', element: suspended(GstInvoiceFormPage) }],
          },
          {
            element: <RequirePermission permission="campaign:read" />,
            children: [{ path: '/campaigns', element: suspended(CampaignsPage) }],
          },
          {
            element: <RequirePermission permission="case:read" />,
            children: [
              { path: '/cases', element: suspended(CasesPage) },
              { path: '/cases/:id', element: suspended(CaseDetailPage) },
            ],
          },
          {
            element: <RequirePermission permission="solution:read" />,
            children: [{ path: '/solutions', element: suspended(SolutionsPage) }],
          },
          {
            element: <RequirePermission permission="quote:read" />,
            children: [
              { path: '/quotes', element: suspended(QuotesPage) },
              { path: '/quotes/:id', element: suspended(QuoteDetailPage) },
            ],
          },
          {
            element: <RequirePermission permission="salesOrder:read" />,
            children: [
              { path: '/sales-orders', element: suspended(SalesOrdersPage) },
              { path: '/sales-orders/:id', element: suspended(SalesOrderDetailPage) },
            ],
          },
          {
            element: <RequirePermission permission="invoice:read" />,
            children: [
              { path: '/invoices', element: suspended(InvoicesPage) },
              { path: '/invoices/:id', element: suspended(InvoiceDetailPage) },
            ],
          },
          {
            element: <RequirePermission permission="payment:read" />,
            children: [
              { path: '/payments-received', element: suspended(PaymentsPage) },
              { path: '/payments-received/:id', element: suspended(PaymentDetailPage) },
            ],
          },
          {
            element: <RequirePermission permission="expense:read" />,
            children: [
              { path: '/expenses', element: suspended(ExpensesPage) },
              { path: '/expenses/:id', element: suspended(ExpenseDetailPage) },
            ],
          },
          {
            element: <RequirePermission permission="recurringExpense:read" />,
            children: [
              { path: '/recurring-expenses', element: suspended(RecurringExpensesPage) },
              { path: '/recurring-expenses/:id', element: suspended(RecurringExpenseDetailPage) },
            ],
          },
          {
            element: <RequirePermission permission="creditNote:read" />,
            children: [
              { path: '/credit-notes', element: suspended(CreditNotesPage) },
              { path: '/credit-notes/:id', element: suspended(CreditNoteDetailPage) },
            ],
          },
          {
            element: <RequirePermission permission="bankAccount:read" />,
            children: [
              { path: '/banking/accounts', element: suspended(BankAccountsPage) },
              { path: '/banking/accounts/:id', element: suspended(BankAccountDetailPage) },
            ],
          },
          {
            element: <RequirePermission permission="bankTransaction:read" />,
            children: [{ path: '/banking/reports', element: suspended(BankingReportsPage) }],
          },
          {
            // Any role that may import something reaches the screen; the
            // dataset buttons are all it offers, and the API refuses a
            // dataset the role cannot write.
            element: <RequirePermission permission="lead:import" />,
            children: [{ path: '/import', element: suspended(ImportPage) }],
          },
          {
            // A Zoho bundle writes Users and Deals in one pass, so this is an
            // administrator's screen, not an importer's.
            element: <RequirePermission permission="organization:manage" />,
            children: [{ path: '/settings/zoho-import', element: suspended(ZohoImportPage) }],
          },
          {
            // Whole-tenant export, same gate as the Zoho migration.
            element: <RequirePermission permission="organization:manage" />,
            children: [{ path: '/settings/backups', element: suspended(BackupsPage) }],
          },
          {
            // Scans across every tenant's Lead/Account/Contact data, same
            // admin gate as the other whole-tenant data screens.
            element: <RequirePermission permission="organization:manage" />,
            children: [{ path: '/settings/duplicates', element: suspended(DuplicatesPage) }],
          },
          {
            // Approvals hang off deals, so they ride on the deal permission
            // rather than one of their own.
            element: <RequirePermission permission="deal:read" />,
            children: [{ path: '/approvals', element: suspended(ApprovalsPage) }],
          },

          { path: '/settings/profile', element: suspended(ProfilePage) },
          {
            // TEAM, not OWN: this screen lists other people, and a rep can
            // only read their own record.
            element: <RequirePermission permission="user:read" minScope="TEAM" />,
            children: [{ path: '/settings/users', element: suspended(UsersPage) }],
          },
          {
            element: <RequirePermission permission="organization:read" />,
            children: [
              { path: '/settings/organization', element: suspended(OrganizationSettingsPage) },
              {
                path: '/settings/document-templates',
                element: suspended(DocumentTemplatesPage),
              },
            ],
          },
          {
            // Every custom module lives under `/m/:moduleRef`, which is why
            // the api name may not collide with a built-in route — a module
            // called `deals` would be unreachable either way.
            element: <RequirePermission permission="customRecord:read" />,
            children: [
              { path: '/m/:moduleRef', element: suspended(CustomModuleListPage) },
              { path: '/m/:moduleRef/:id', element: suspended(CustomRecordDetailPage) },
            ],
          },
          {
            // Read is enough to reach the builder — every role can see what
            // modules exist; the write controls need `customField:manage`.
            element: <RequirePermission permission="customField:read" />,
            children: [{ path: '/settings/modules', element: suspended(ModuleBuilderPage) }],
          },
          {
            // Read is enough to see it; the form disables itself without
            // `organization:manage`, which is what the API enforces.
            element: <RequirePermission permission="quote:read" />,
            children: [{ path: '/settings/documents', element: suspended(BillingSettingsPage) }],
          },
          {
            element: <RequirePermission permission="auditLog:read" />,
            children: [{ path: '/settings/audit-log', element: suspended(AuditLogPage) }],
          },
          {
            element: <RequirePermission permission="workflow:read" />,
            children: [
              { path: '/settings/workflows', element: suspended(WorkflowsPage) },
              { path: '/settings/assignment-rules', element: suspended(AssignmentRulesPage) },
            ],
          },
          {
            // Read is enough to reach the screen — every role can see which
            // fields exist; the write controls are hidden without `manage`.
            element: <RequirePermission permission="customField:read" />,
            children: [{ path: '/settings/custom-fields', element: suspended(CustomFieldsPage) }],
          },
          {
            element: <RequirePermission permission="email:read" />,
            children: [
              { path: '/settings/email-templates', element: suspended(EmailTemplatesPage) },
            ],
          },
          { path: '/settings', element: <Navigate to="/settings/profile" replace /> },
          { path: '*', element: suspended(NotFoundPage) },
        ],
      },
    ],
  },
]);
